import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

import { dashboardQueries } from '../data-access/queries/dashboard.queries';
import { DashboardClause } from '../data-access/types/stats';
import {
  buildOrganisationFilterOptions,
  getDefaultOrganisationIds,
} from '../utils/organisation-filter';

import { useQueriesWithGlobal } from '@data-access';

export function useDashboardOrganisations() {
  const queries = useQueriesWithGlobal(dashboardQueries);

  return useQuery(queries.organisations());
}

export function useOrganisationFilterState(
  organisations: ReturnType<typeof useDashboardOrganisations>['data'],
  clause: DashboardClause = 'gp',
) {
  const results = organisations?.results ?? [];

  const defaultOrganisationIds = useMemo(
    () => getDefaultOrganisationIds(results, clause),
    [clause, results],
  );

  const organisationOptions = useMemo(
    () => buildOrganisationFilterOptions(results, defaultOrganisationIds),
    [defaultOrganisationIds, results],
  );

  return {
    defaultOrganisationIds,
    organisationOptions,
    organisations: results,
  };
}

export function useInitializeOrganisationSelection(
  organisationIds: number[] | null,
  setOrganisationIds: (ids: number[]) => void,
  organisations: ReturnType<typeof useDashboardOrganisations>['data'],
  clause: DashboardClause = 'gp',
) {
  const previousClauseRef = useRef<DashboardClause | null>(null);

  useEffect(() => {
    if (!organisations) {
      return;
    }

    const defaults = getDefaultOrganisationIds(
      organisations.results ?? [],
      clause,
    );
    const clauseChanged =
      previousClauseRef.current !== null &&
      previousClauseRef.current !== clause;

    previousClauseRef.current = clause;

    if (organisationIds === null || clauseChanged) {
      setOrganisationIds(defaults);
    }
  }, [clause, organisationIds, organisations, setOrganisationIds]);
}
